import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Divider,
  Chip,
  Button,
  TextField,
  Card,
  CardContent,
  CardHeader,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  IconButton,
  useTheme,
  alpha,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
  Tabs,
  Tab
} from '@mui/material';
import {
  Person as PersonIcon,
  AccessTime as AccessTimeIcon,
  Send as SendIcon,
  ExpandMore as ExpandMoreIcon,
  MedicalInformation as MedicalInformationIcon,
  Assignment as AssignmentIcon,
  Medication as MedicationIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  Call as CallIcon,
  Videocam as VideocamIcon,
  NoteAdd as NoteAddIcon,
  LocalPharmacy as LocalPharmacyIcon,
  History as HistoryIcon
} from '@mui/icons-material';
import IntercomEmbed from '../IntercomEmbed/IntercomEmbed';
import TwilioVideo from '../TwilioVideo/TwilioVideo';
import TwilioVoice from '../TwilioVoice/TwilioVoice';
import ClinicalNotepad from '../ClinicalNotepad/ClinicalNotepad';
import PrescriptionForm from '../Prescriptions/PrescriptionForm';
import PrescriptionHistory from '../Prescriptions/PrescriptionHistory';
import { 
  closeIntercomConversation, 
  getCurrentAdminId,
  extractHealthDataFromConversation,
  getPatientInfoFromConversation
} from '../../services/intercomService';

const ConsultationDetail = ({ consultation, onClose }) => {
  const theme = useTheme();
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [intercomAppId, setIntercomAppId] = useState(process.env.REACT_APP_INTERCOM_APP_ID || '');
  const [intercomAccessToken, setIntercomAccessToken] = useState(process.env.REACT_APP_INTERCOM_ACCESS_TOKEN || '');
  const [healthData, setHealthData] = useState({
    complaints: '',
    start_time: '',
    characteristics: '',
    pain_severity: '',
    other_symptoms: '',
    medication_taken: '',
    improvement_with_medication: '',
    additional_illnesses: '',
    additional_medications: ''
  });
  const [patientInfo, setPatientInfo] = useState({
    name: '',
    age: '',
    gender: ''
  });
  const [conversationLoaded, setConversationLoaded] = useState(false);
  const [communicationMode, setCommunicationMode] = useState('chat'); // 'chat', 'call', or 'video'
  const [activeTab, setActiveTab] = useState(0);
  const [prescriptions, setPrescriptions] = useState([]);

  useEffect(() => {
    // Log the Intercom credentials for debugging
    console.log('Intercom App ID:', process.env.REACT_APP_INTERCOM_APP_ID);
    console.log('Intercom Access Token:', process.env.REACT_APP_INTERCOM_ACCESS_TOKEN ? 'Present (hidden for security)' : 'Not found');
    
    // Update the state with the environment variables
    setIntercomAppId(process.env.REACT_APP_INTERCOM_APP_ID || '');
    setIntercomAccessToken(process.env.REACT_APP_INTERCOM_ACCESS_TOKEN || '');
    
    if (consultation) {
      // In a real implementation, this would fetch the conversation from Intercom
      // and extract the health data and patient info
      
      // For now, we'll use the mock data
      setHealthData({
        complaints: consultation.complaints || '',
        start_time: consultation.start_time || '',
        characteristics: consultation.characteristics || '',
        pain_severity: consultation.pain_severity || '',
        other_symptoms: consultation.other_symptoms || '',
        medication_taken: consultation.medication_taken || '',
        improvement_with_medication: consultation.improvement_with_medication || '',
        additional_illnesses: consultation.additional_illnesses || '',
        additional_medications: consultation.additional_medications || ''
      });
      
      setPatientInfo({
        name: consultation.patientName || '',
        age: consultation.patientAge || '',
        gender: consultation.patientGender || ''
      });
    }
  }, [consultation]);

  const handleSendMessage = async () => {
    if (!message.trim()) return;
    
    setSending(true);
    setError(null);
    
    try {
      // In a real app, this would be an API call to Intercom
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Clear the message input
      setMessage('');
      
      // Add the message to the conversation (would be handled by the API in a real app)
      console.log('Message sent:', message);
      
      setSending(false);
    } catch (err) {
      console.error('Error sending message:', err);
      setError('Failed to send message. Please try again.');
      setSending(false);
    }
  };

  const handleResolveConsultation = async () => {
    if (!consultation || !consultation.id) return;
    
    setResolving(true);
    setError(null);
    
    try {
      const adminId = await getCurrentAdminId();
      
      // Close the conversation in Intercom
      await closeIntercomConversation(consultation.id, adminId);
      
      setResolveDialogOpen(false);
      setResolving(false);
      
      // Return to the list
      if (onClose) onClose();
    } catch (err) {
      console.error('Error resolving consultation:', err);
      setError('Failed to resolve consultation. Please try again.');
      setResolving(false);
    }
  };

  const handleConversationLoaded = () => {
    console.log('Intercom conversation loaded successfully');
    setConversationLoaded(true);
    
    // In a real implementation, you would extract health data from the conversation
    // using the extractHealthDataFromConversation function
    
    // For now, we'll just log a success message
    console.log('Conversation loaded for patient:', patientInfo.name);
  };

  const handleStartVoiceCall = () => {
    setCommunicationMode('call');
  };

  const handleStartVideoCall = () => {
    setCommunicationMode('video');
  };

  const handleReturnToChat = () => {
    setCommunicationMode('chat');
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handlePrescriptionCreated = (newPrescription) => {
    setPrescriptions(prevPrescriptions => [newPrescription, ...prevPrescriptions]);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const options = { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleString(undefined, options);
  };

  if (!consultation) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6">No consultation selected</Typography>
      </Paper>
    );
  }

  // Render different communication modes
  const renderCommunicationInterface = () => {
    switch (communicationMode) {
      case 'call':
        return (
          <Box sx={{ height: 500, mb: 3 }}>
            <TwilioVoice 
              consultation={consultation} 
              onClose={handleReturnToChat} 
            />
          </Box>
        );
      case 'video':
        return (
          <Box sx={{ height: 500, mb: 3 }}>
            <TwilioVideo 
              consultation={consultation} 
              onClose={handleReturnToChat} 
            />
          </Box>
        );
      case 'chat':
      default:
        return (
          <Box sx={{ height: 500, mb: 3 }}>
            <IntercomEmbed 
              conversationId={consultation.id}
              appId={intercomAppId}
              accessToken={intercomAccessToken}
              onConversationLoaded={handleConversationLoaded}
            />
          </Box>
        );
    }
  };

  // Render clinical tools based on active tab
  const renderClinicalTools = () => {
    switch (activeTab) {
      case 0: // Clinical Notes
        return (
          <ClinicalNotepad 
            patientId={consultation.patientId || consultation.id} 
            consultationId={consultation.id} 
          />
        );
      case 1: // New Prescription
        return (
          <PrescriptionForm 
            patientId={consultation.patientId || consultation.id} 
            consultationId={consultation.id}
            onPrescriptionCreated={handlePrescriptionCreated}
          />
        );
      case 2: // Prescription History
        return (
          <PrescriptionHistory 
            patientId={consultation.patientId || consultation.id}
            prescriptions={prescriptions}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box sx={{ p: 0 }}>
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar sx={{ bgcolor: theme.palette.primary.main, mr: 2 }}>
              <PersonIcon />
            </Avatar>
            <Box>
              <Typography variant="h5" component="h2">
                {patientInfo.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {patientInfo.age} years, {patientInfo.gender}
              </Typography>
            </Box>
          </Box>
          <Chip 
            label={consultation.status === 'new' ? 'New' : consultation.status === 'active' ? 'Active' : 'Resolved'} 
            color={consultation.status === 'new' ? 'primary' : consultation.status === 'active' ? 'success' : 'default'}
            sx={{ fontWeight: 'bold' }}
          />
        </Box>

        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <AccessTimeIcon fontSize="small" sx={{ mr: 1, color: theme.palette.info.main, fontSize: '0.9rem' }} />
          Started: {formatDate(consultation.timestamp)}
        </Typography>

        {/* Communication preference indicator */}
        {consultation.communicationPreference && (
          <Box sx={{ mb: 2 }}>
            <Chip 
              icon={consultation.communicationPreference === 'video' ? <VideocamIcon /> : 
                    consultation.communicationPreference === 'call' ? <CallIcon /> : 
                    <SendIcon />}
              label={`Preferred: ${consultation.communicationPreference === 'video' ? 'Video Call' : 
                                   consultation.communicationPreference === 'call' ? 'Voice Call' : 
                                   'Chat'}`}
              color="info"
              variant="outlined"
              sx={{ mr: 1 }}
            />
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardHeader
                avatar={<MedicalInformationIcon color="primary" />}
                title="Health Information"
                sx={{ pb: 1 }}
              />
              <CardContent sx={{ pt: 0 }}>
                <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5, fontWeight: 'bold' }}>
                  Main Complaint:
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {healthData.complaints || 'Not specified'}
                </Typography>

                <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 'bold' }}>
                  Started:
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {healthData.start_time || 'Not specified'}
                </Typography>

                <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 'bold' }}>
                  Characteristics:
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {healthData.characteristics || 'Not specified'}
                </Typography>

                <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 'bold' }}>
                  Pain Severity:
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {healthData.pain_severity || 'Not specified'}
                </Typography>

                <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 'bold' }}>
                  Other Symptoms:
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {healthData.other_symptoms || 'Not specified'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardHeader
                avatar={<MedicationIcon color="primary" />}
                title="Medication Information"
                sx={{ pb: 1 }}
              />
              <CardContent sx={{ pt: 0 }}>
                <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5, fontWeight: 'bold' }}>
                  Medication Taken:
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {healthData.medication_taken || 'Not specified'}
                </Typography>

                <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 'bold' }}>
                  Improvement with Medication:
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {healthData.improvement_with_medication || 'Not specified'}
                </Typography>

                <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 'bold' }}>
                  Additional Illnesses:
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {healthData.additional_illnesses || 'Not specified'}
                </Typography>

                <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 'bold' }}>
                  Additional Medications:
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {healthData.additional_medications || 'Not specified'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>

      {/* Communication mode buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        {communicationMode !== 'chat' && (
          <Button 
            variant="outlined" 
            onClick={handleReturnToChat}
            startIcon={<SendIcon />}
            sx={{ mr: 1 }}
          >
            Return to Chat
          </Button>
        )}
        
        {communicationMode !== 'call' && consultation.communicationPreference === 'call' && (
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleStartVoiceCall}
            startIcon={<CallIcon />}
            sx={{ mr: 1 }}
          >
            Start Voice Call
          </Button>
        )}
        
        {communicationMode !== 'video' && consultation.communicationPreference === 'video' && (
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleStartVideoCall}
            startIcon={<VideocamIcon />}
          >
            Start Video Call
          </Button>
        )}
      </Box>

      {/* Communication interface (chat, voice, or video) */}
      {renderCommunicationInterface()}

      {/* Clinical Tools Tabs */}
      <Paper elevation={3} sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="fullWidth"
          indicatorColor="primary"
          textColor="primary"
          aria-label="clinical tools tabs"
        >
          <Tab 
            icon={<NoteAddIcon />} 
            label="Clinical Notes" 
            id="clinical-tab-0" 
            aria-controls="clinical-tabpanel-0" 
          />
          <Tab 
            icon={<LocalPharmacyIcon />} 
            label="New Prescription" 
            id="clinical-tab-1" 
            aria-controls="clinical-tabpanel-1" 
          />
          <Tab 
            icon={<HistoryIcon />} 
            label="Prescription History" 
            id="clinical-tab-2" 
            aria-controls="clinical-tabpanel-2" 
          />
        </Tabs>
        
        <Box role="tabpanel" id={`clinical-tabpanel-${activeTab}`} aria-labelledby={`clinical-tab-${activeTab}`}>
          {renderClinicalTools()}
        </Box>
      </Paper>

      {/* Action buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
        <Button 
          variant="contained" 
          color="primary"
          onClick={() => setResolveDialogOpen(true)}
          startIcon={<CheckCircleIcon />}
          disabled={resolving}
          sx={{ 
            ml: 2,
            bgcolor: theme.palette.success.main,
            '&:hover': {
              bgcolor: theme.palette.success.dark
            }
          }}
        >
          {resolving ? 'Resolving...' : 'Resolve Consultation'}
        </Button>
      </Box>

      {/* Resolve confirmation dialog */}
      <Dialog
        open={resolveDialogOpen}
        onClose={() => setResolveDialogOpen(false)}
      >
        <DialogTitle>Resolve Consultation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to resolve this consultation? This will close the conversation and mark it as resolved.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResolveDialogOpen(false)} color="primary">
            Cancel
          </Button>
          <Button 
            onClick={handleResolveConsultation} 
            color="primary" 
            variant="contained"
            disabled={resolving}
          >
            {resolving ? 'Resolving...' : 'Resolve'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ConsultationDetail; 